import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit, Trash2, Calendar, Eye, Star, Globe, MapPin } from 'lucide-react';
import AddEditFestivalModal from '../components/modals/AddEditFestivalModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ApiService from '../services/api';

const Festivals = () => {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const categories = ['All', 'Religious', 'Cultural', 'Seasonal', 'Regional', 'Traditional', 'Spiritual', 'Other'];

  // Helper function to strip HTML tags and get plain text for preview
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    const plainText = stripHtmlTags(text);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    fetchFestivals();
  }, [currentPage, categoryFilter, statusFilter]);

  useEffect(() => {
    // Reset to page 1 when search term changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchFestivals();
    }
  }, [searchTerm]);

  const fetchFestivals = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (categoryFilter !== 'All') {
        params.category = categoryFilter;
      }
      
      if (statusFilter !== 'All') {
        params.isActive = statusFilter === 'Active';
      }
      
      const response = await ApiService.getAllFestivals(params);
      if (response.success) {
        setFestivals(response.data || []);
        setPagination(response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      }
    } catch (error) {
      console.error('Error fetching festivals:', error);
      setFestivals([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filter (applied to current page data)
  const filteredFestivals = festivals.filter(festival => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      festival.name.toLowerCase().includes(searchLower) ||
      stripHtmlTags(festival.description).toLowerCase().includes(searchLower) ||
      stripHtmlTags(festival.significance).toLowerCase().includes(searchLower)
    );
  });

  const handleAddFestival = () => {
    setSelectedFestival(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEditFestival = (festival) => {
    setSelectedFestival(festival);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDeleteFestival = (festival) => {
    setSelectedFestival(festival);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteFestival = async () => {
    try {
      const response = await ApiService.deleteFestival(selectedFestival.id);
      if (response.success) {
        await fetchFestivals();
        setIsDeleteModalOpen(false);
        setSelectedFestival(null);
      }
    } catch (error) {
      console.error('Error deleting festival:', error);
    }
  };

  const handleModalSave = async () => {
    await fetchFestivals();
    setIsModalOpen(false);
    setSelectedFestival(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUpcomingIndicator = (startDate) => {
    const today = new Date();
    const festivalDate = new Date(startDate);
    const diffTime = festivalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return null;
    if (diffDays === 0) return <span className="text-green-600 text-xs font-medium">Today</span>;
    if (diffDays <= 7) return <span className="text-orange-600 text-xs font-medium">In {diffDays} days</span>;
    if (diffDays <= 30) return <span className="text-blue-600 text-xs font-medium">In {diffDays} days</span>;
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Festivals Management</h1>
            <p className="text-gray-600 mt-1">Manage festivals, dates, and cultural information</p>
          </div>
          <button
            onClick={handleAddFestival}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Festival
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search festivals..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <div className="flex items-center text-sm text-gray-600">
            <Filter className="w-4 h-4 mr-2" />
            {pagination.totalItems} total festivals
          </div>
        </div>
      </div>

      {/* Festivals Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredFestivals.map((festival) => (
          <div key={festival.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            {/* Festival Image */}
            <div className="relative h-48 bg-gradient-to-r from-indigo-500 to-purple-600">
              {festival.imageUrl ? (
                <img
                  src={festival.imageUrl}
                  alt={festival.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white">
                  <Star className="w-12 h-12" />
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  festival.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {festival.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Priority Badge */}
              {festival.priority > 0 && (
                <div className="absolute top-3 left-3">
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                    Priority {festival.priority}
                  </span>
                </div>
              )}
            </div>

            <div className="p-6">
              {/* Festival Header */}
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{festival.name}</h3>
                {festival.category && (
                  <span className="inline-block bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-medium">
                    {festival.category}
                  </span>
                )}
              </div>

              {/* Description */}
              {festival.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {truncateText(festival.description, 120)}
                </p>
              )}

              {/* Festival Date */}
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {formatDate(festival.startDate)}
                  {festival.endDate && ` - ${formatDate(festival.endDate)}`}
                </span>
                {getUpcomingIndicator(festival.startDate)}
              </div>

              {/* Regions */}
              {festival.regions && festival.regions.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {festival.regions.slice(0, 2).join(', ')}
                    {festival.regions.length > 2 && ` +${festival.regions.length - 2} more`}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditFestival(festival)}
                    className="text-indigo-600 hover:text-indigo-800 p-1 rounded transition-colors"
                    title="Edit Festival"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteFestival(festival)}
                    className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                    title="Delete Festival"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs text-gray-500">
                  {festival.isRecurring ? 'Recurring' : 'One-time'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredFestivals.length === 0 && !loading && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No festivals found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || categoryFilter !== 'All' || statusFilter !== 'All'
              ? 'Try adjusting your filters to see more results.'
              : 'Get started by adding your first festival.'}
          </p>
          {!searchTerm && categoryFilter === 'All' && statusFilter === 'All' && (
            <button
              onClick={handleAddFestival}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Your First Festival
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col items-center mt-8 space-y-3">
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} festivals
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={pagination.currentPage === 1}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            
            {/* Smart Page Number Display */}
            {(() => {
              const pages = [];
              const maxPagesToShow = 5;
              const totalPages = pagination.totalPages;
              const currentPage = pagination.currentPage;
              
              if (totalPages <= maxPagesToShow) {
                // Show all pages if total is less than max
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Always show first page
                pages.push(1);
                
                // Calculate range around current page
                let startPage = Math.max(2, currentPage - 1);
                let endPage = Math.min(totalPages - 1, currentPage + 1);
                
                // Adjust if current page is near the start
                if (currentPage <= 3) {
                  startPage = 2;
                  endPage = 4;
                }
                
                // Adjust if current page is near the end
                if (currentPage >= totalPages - 2) {
                  startPage = totalPages - 3;
                  endPage = totalPages - 1;
                }
                
                // Add ellipsis if needed at start
                if (startPage > 2) {
                  pages.push('...');
                }
                
                // Add middle pages
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                
                // Add ellipsis if needed at end
                if (endPage < totalPages - 1) {
                  pages.push('...');
                }
                
                // Always show last page
                pages.push(totalPages);
              }
              
              return pages.map((page, index) => {
                if (page === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 text-sm border border-gray-300 rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              });
            })()}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddEditFestivalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
        festival={selectedFestival}
        isEditing={isEditing}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteFestival}
        title="Delete Festival"
        message={`Are you sure you want to delete "${selectedFestival?.name}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default Festivals;